import React, { useState, useEffect, useCallback } from 'react';
import { api } from '../../utils/api';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogActions from '@mui/material/DialogActions';
import { deleteUser } from '../../utils/superadminApi';
import { useAuth } from '../../context/AuthContext'; // To get current user for disabling self-demote

const UserList = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // The user pending a delete confirmation, and whether the delete is in flight.
  const [userToDelete, setUserToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const { user: currentUser } = useAuth(); // Get the currently logged-in admin user

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await api.get('/admin/users');
      setUsers(response.data);
    } catch (err) {
      console.error("Error fetching users:", err);
      setError(err.response?.data?.message || 'Failed to load users.');
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const handlePromote = async (userId) => {
    console.log(`Attempting to promote user: ${userId}`);
    try {
      await api.put(`/admin/users/${userId}/promote`);
      // Refetch users to show updated status
      fetchUsers(); 
    } catch (err) {
      console.error("Error promoting user:", err);
      // Optionally, set an error message to display to the user
      setError(err.response?.data?.message || 'Failed to promote user.');
    }
  };

  const handleDemote = async (userId) => {
    console.log(`Attempting to demote user: ${userId}`);
    try {
      await api.put(`/admin/users/${userId}/demote`);
      // Refetch users to show updated status
      fetchUsers();
    } catch (err) {
      console.error("Error demoting user:", err);
      setError(err.response?.data?.message || 'Failed to demote user.');
    }
  };

  const handleConfirmDelete = async () => {
    if (!userToDelete) return;
    setDeleting(true);
    setError(null);
    try {
      await deleteUser(userToDelete.id);
      setUserToDelete(null);
      fetchUsers();
    } catch (err) {
      console.error('Error deleting user:', err);
      // Surface the sole-owner guidance (409) or any other server message.
      const orgs = err.response?.data?.organizations;
      const base = err.response?.data?.message || 'Failed to delete user.';
      setError(orgs && orgs.length
        ? `${base} (${orgs.map((o) => o.name || o.id).join(', ')})`
        : base);
      setUserToDelete(null);
    }
    setDeleting(false);
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', my: 3 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Paper elevation={3} sx={{ mt: 4, p: 3 }}>
      <Typography variant="h5" gutterBottom sx={{ mb: 2 }}>
        User Management
      </Typography>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      <TableContainer component={Paper} variant="outlined">
        <Table sx={{ minWidth: 650 }} aria-label="user list table">
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Email</TableCell>
              <TableCell align="center">Admin</TableCell>
              <TableCell align="center">Sessions</TableCell>
              <TableCell>Registered</TableCell>
              <TableCell align="center">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {users.map((user) => (
              <TableRow key={user.id}>
                <TableCell component="th" scope="row">
                  {user.name}
                </TableCell>
                <TableCell>{user.email}</TableCell>
                <TableCell align="center">{user.isAdmin ? 'Yes' : 'No'}</TableCell>
                <TableCell align="center">{user.sessionCount || 0}</TableCell>
                <TableCell>{new Date(user.createdAt).toLocaleDateString()}</TableCell>
                <TableCell align="center">
                  <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                    {user.isAdmin ? (
                      <Button
                        variant="outlined"
                        color="secondary"
                        size="small"
                        onClick={() => handleDemote(user.id)}
                        // Disable demoting oneself if it's the current user
                        disabled={currentUser?.id === user.id}
                      >
                        Demote
                      </Button>
                    ) : (
                      <Button
                        variant="contained"
                        color="primary"
                        size="small"
                        onClick={() => handlePromote(user.id)}
                      >
                        Promote
                      </Button>
                    )}
                    <Button
                      variant="outlined"
                      color="error"
                      size="small"
                      onClick={() => setUserToDelete(user)}
                      // A superadmin cannot delete their own account.
                      disabled={currentUser?.id === user.id}
                    >
                      Delete
                    </Button>
                  </Box>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      {users.length === 0 && !loading && (
        <Typography sx={{ textAlign: 'center', mt: 2 }}>No users found.</Typography>
      )}

      <Dialog open={Boolean(userToDelete)} onClose={() => !deleting && setUserToDelete(null)}>
        <DialogTitle>Delete user?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This permanently deletes{' '}
            <strong>{userToDelete?.name || userToDelete?.email}</strong>
            {userToDelete?.email ? ` (${userToDelete.email})` : ''}. This cannot be undone. If this
            user is the sole owner of an organization, delete that organization first.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setUserToDelete(null)} disabled={deleting}>
            Cancel
          </Button>
          <Button onClick={handleConfirmDelete} color="error" variant="contained" disabled={deleting}>
            {deleting ? <CircularProgress size={20} /> : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

export default UserList; 